/** TWILIO_APN_CREDENTIAL control: upload the Apple APNs certificate + private key (PEM) so the
 *  backend creates the Twilio Push Credential (stores only the resulting SID). The key material is
 *  used, never displayed back or stored.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem, TwilioApnDetail } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

export function TwilioApnCredentialControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const detail = item.detail as unknown as TwilioApnDetail;
  const [certificate, setCertificate] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [sandbox, setSandbox] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      items.createTwilioApnCredential(topId, item.id, {
        certificate: certificate.trim(),
        private_key: privateKey.trim(),
        sandbox,
      }),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('APNs push credential created.', 'success');
        setCertificate('');
        setPrivateKey('');
        onChanged();
      } else {
        setOpError(typeof res.detail === 'string' ? res.detail : null);
        snackbar.show(
          res.reason === 'not_configured' ? 'Twilio is not configured.' : 'Could not create the credential.',
          'error',
        );
      }
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not create the credential.', 'error'),
  });

  if (detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Twilio isn&apos;t configured on the server.</Alert>
      </Box>
    );
  }

  const existing = detail?.apn_credential_sid;
  const canSubmit = !!certificate.trim() && !!privateKey.trim() && !create.isPending;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {opError && (
          <Alert severity="error" onClose={() => setOpError(null)} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {opError}
          </Alert>
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          {existing ? (
            <CheckCircleIcon fontSize="small" color="success" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
          )}
          <Typography variant="body2">
            {existing ? `APNs credential set (${existing})` : 'No APNs push credential yet'}
          </Typography>
        </Stack>

        {canManage && (
          <>
            <Typography variant="caption" color="text.secondary">
              Paste the APNs certificate + private key (PEM) from the mobile devs. They are used to
              create the Twilio credential and are not stored.
            </Typography>
            <TextField
              size="small"
              label="APNs certificate (PEM)"
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
              multiline
              minRows={2}
              disabled={create.isPending}
              fullWidth
            />
            <TextField
              size="small"
              label="APNs private key (PEM)"
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              multiline
              minRows={2}
              disabled={create.isPending}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={sandbox}
                  onChange={(e) => setSandbox(e.target.checked)}
                  disabled={create.isPending}
                />
              }
              label="Sandbox (development) APNs"
            />
            <Box>
              <Button
                size="small"
                variant="contained"
                onClick={() => create.mutate()}
                disabled={!canSubmit}
                startIcon={create.isPending ? <CircularProgress size={14} /> : undefined}
              >
                {existing ? 'Replace credential' : 'Create credential'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}
